import React, { useEffect, useRef } from 'react';
import './Sidebar';
import { useDispatch, useSelector } from 'react-redux';
import { incrementCharacterIndex } from './redux/characterIndex';
import { enableButton, disableAllButtons } from './redux/buttonstatus';
import { deactivateCharacter, incrementCount } from './redux/stageList';
import { setCharacterLabel } from './redux/runDisplay';
import {
  playRandomAudio,
  characterSelectionClips,
  completionClips,
} from './AudioClips';
import {
  spinWheel,
  WHEEL_SELECTION_TIMINGS,
} from './wheelAnimation';

// Timing between multiple character selections
const BETWEEN_FIRST_SECOND_SELECTION_MS = 2000;
const BETWEEN_SELECTIONS_MS = 1000;

const PickCharacter = ({ handleShowImage, numberOfPlayers }) => {
  const dispatch = useDispatch();
  const stageListData = useSelector((state) => state.stageList.stageListData);
  const actualCharacterIndex = useSelector(
    (state) => state.characterIndex.characterIndexCount,
  );
  const characterIndexRef = useRef(actualCharacterIndex); // Initialize with the current value of the selector.
  const characterButtonStatus = useSelector(
    (state) => state.buttonStatus.characterButton,
  );
  const stageIndex = useSelector((state) => state.stageIndex.stageIndexCount);
  const spinCursorRef = useRef(0);

  const numberOfStages = 17; // Define the number of stages globally
  // Calculate completion threshold based on number of players
  let characterIndexThreshold;
  if (numberOfPlayers === 1) {
    characterIndexThreshold = numberOfStages * 1 + 13; // 30
  } else if (numberOfPlayers === 2) {
    characterIndexThreshold = numberOfStages * 2 + 13; // 47
  } else if (numberOfPlayers === 3) {
    characterIndexThreshold = numberOfStages * 3 + 13; // 64
  } else if (numberOfPlayers === 4) {
    characterIndexThreshold = numberOfStages * 4 + 13; // 81
  }

  const activeCharacters = stageListData.flatMap((stage) =>
    stage.characters
      .filter((character) => character.isActive)
      .map((character) => character.character),
  );

  const emitCharacterEvent = (type, character) => {
    document.dispatchEvent(
      new CustomEvent(type, {
        detail: { character },
      }),
    );
  };

  useEffect(() => {
    characterIndexRef.current = actualCharacterIndex;
  }, [actualCharacterIndex]);

  const getRotatedCharacters = (characters, startIndex) => {
    if (!characters.length) {
      return characters;
    }

    const normalizedStartIndex =
      ((startIndex % characters.length) + characters.length) % characters.length;

    return [
      ...characters.slice(normalizedStartIndex),
      ...characters.slice(0, normalizedStartIndex),
    ];
  };

  const runMultipleTimes = (func) => {
    emitCharacterEvent('tmnt-character-run-start');
    let remainingRuns = numberOfPlayers; // Number of times to run based on selected players.
    let selectionCount = 0;
    // console.log(`Number of Players: ${numberOfPlayers}`);

    let currentOrder = getRotatedCharacters(activeCharacters, spinCursorRef.current);

    const runFunc = (characters) => {
      func(characters, (selectedCharacter) => {
        const filteredCharacters = characters.filter(
          (character) => character !== selectedCharacter.character,
        );

        if (filteredCharacters.length > 0) {
          const selectedIndex = characters.indexOf(selectedCharacter.character);
          spinCursorRef.current =
            selectedIndex >= 0 ? (selectedIndex + 1) % characters.length : 0;
        }

        selectionCount++;
        remainingRuns--;
        if (remainingRuns > 0) {
          const pauseMs =
            selectionCount === 1
              ? BETWEEN_FIRST_SECOND_SELECTION_MS
              : BETWEEN_SELECTIONS_MS;
          const nextCharacters = getRotatedCharacters(
            filteredCharacters,
            spinCursorRef.current,
          );
          setTimeout(() => runFunc(nextCharacters), pauseMs);
        } else {
          setTimeout(() => {
            dispatch(enableButton({ buttonId: 'removeCharacterButton' }));

            if (stageIndex < 13) {
              dispatch(enableButton({ buttonId: 'stageButton' }));
            } else if (characterIndexRef.current < characterIndexThreshold) {
              dispatch(enableButton({ buttonId: 'characterButton' }));
            } else {
              // console.log(
              // //   `CharacterIndexRef.current: ${characterIndexRef.current}`,
              // );
              // Display the Cowabunga Logo or perform final action.
              playRandomAudio(completionClips);
              handleShowImage();
            }
          }, 1000);
        }
      });
    };

    runFunc(currentOrder);
  };

  const handleRunMultipleTimes = () => {
    playRandomAudio(characterSelectionClips); // Play selection audio.
    runMultipleTimes((activeCharactersList, callback) => {
      handleCharacterSelection(activeCharactersList, callback);
    });
  };

  const handleCharacterSelection = (activeCharactersList, callback) => {
    // console.log(activeCharactersList);
    // console.log(characterIndexRef.current);
    let headerCharacters = document.getElementById('headerCharacters');
    dispatch(disableAllButtons());

    // Handler for each spin step - called for each character that appears
    const onSpinStep = (character) => {
      emitCharacterEvent('tmnt-character-spin', character);
    };

    // Handler for when the spin completes and a character is selected
    const onSelect = (selectedCharacterName) => {
      emitCharacterEvent('tmnt-character-selected', selectedCharacterName);
      let characters = document.getElementById(characterIndexRef.current);
      characters.textContent = selectedCharacterName;
      dispatch(
        setCharacterLabel({
          slotId: characterIndexRef.current,
          label: selectedCharacterName,
        }),
      );

      // Find the character in our data and update counts
      const selectedCharacter = stageListData
        .flatMap((stage) => stage.characters)
        .find((character) => character.character === selectedCharacterName);

      if (selectedCharacter) {
        dispatch(incrementCount(selectedCharacter.character));
        dispatch(
          deactivateCharacter({
            character: selectedCharacter.character,
            numberOfPlayers,
          }),
        );
      }

      // Increment character index if we haven't reached the threshold
      if (characterIndexRef.current < characterIndexThreshold) {
        dispatch(incrementCharacterIndex());
      }

      if (typeof callback === 'function') {
        callback(selectedCharacter);
      }
    };

    // Start the wheel spin animation
    spinWheel(
      headerCharacters,
      activeCharactersList,
      WHEEL_SELECTION_TIMINGS,
      onSpinStep,
      onSelect,
    );
  };

  return (
    <div>
      <header>Character Picker</header>
      <div className='wheel-display' aria-live='polite'>
        <span id='headerCharacters'>?</span>
      </div>
      <button
        onClick={handleRunMultipleTimes}
        disabled={characterButtonStatus === 'disabled'}
        className='button'
        id='characterButton'
      >
        Start
      </button>
    </div>
  );
};

export default PickCharacter;
